import { apiBaseUrl } from '../authConfig';
import { ApiCallError, api, engineerToken } from './client';
import type { ProductManifest } from './types';

export type ReleaseStatus = 'Draft' | 'Current' | 'Superseded' | 'Withdrawn';

export interface Release {
  version: string;
  status: ReleaseStatus;
  releasedAt: string;
  releasedBy: string;
  notes: string;
  withdrawnReason?: string;
  withdrawnAt?: string;
  withdrawnBy?: string;
  sizeBytes: number;
  sha256: string;
  blobPath: string;
  fileName: string;
  solutionId: string;
  tenantWideDeploy: boolean;
  apiPermissions: Array<{ resource: string; scope: string }>;
}

/** Guardian mirror outcome of a change. A warning only: the change is already saved. */
export interface GuardianSync { ok: boolean; error?: string; sent?: string[]; notSent?: string[] }

/** Deployment manifest outcome of a change. A warning only: the change is already saved. */
export interface ManifestSync { ok: boolean; updated: boolean; message?: string }

export interface ProductReleaseSummary {
  currentVersion: string | null;
  releasedAt: string | null;
  releasedBy: string | null;
  draftCount: number;
  installedOn: number | null;
  outdated: number | null;
}

/** release is null when the product's release index could not be loaded. */
export type ProductWithRelease = ProductManifest & { release?: ProductReleaseSummary | null };

export interface ReleaseList {
  productId: string;
  displayName: string;
  guardianAppId: string;
  current: string | null;
  releases: Release[];
}

/** Result of every release-changing call: the fresh list plus the Guardian mirror outcome. */
export interface ReleaseChange {
  current: string | null;
  releases: Release[];
  guardianSync?: GuardianSync;
  manifestSync?: ManifestSync;
  release?: Release;
  promoted?: string | null;
}

export interface PackageFacts {
  version: string; solutionId: string; tenantWideDeploy: boolean; apiPermissions: Array<{ resource: string; scope: string }>; sizeBytes: number; sha256: string;
}

/**
 * Result of /check. When the size, zip or manifest check fails early the API returns the failed
 * rows with no facts, and may omit version and fileName, so all three are optional.
 */
export interface PackageCheck {
  ok: boolean;
  checks: Array<{ id: string; label: string; ok: boolean; detail?: string }>;
  facts?: PackageFacts;
  replacesDraft: boolean;
  stored: boolean;
  version?: string;
  fileName?: string;
  /** Present when the package passed and was staged; register must send it back. */
  sha256?: string;
}

/**
 * Body of the register call; built only by buildRegisterBody() in src/releases/publishPackage.ts.
 * sha256 is the one /check returned (it locates the staged package); makeCurrent is sent only as true.
 */
export interface RegisterReleaseBody { version: string; sha256: string; fileName?: string; notes: string; makeCurrent?: true }

const enc = encodeURIComponent;
const base = (productId: string) => `products/${enc(productId)}/releases`;

export const listProducts = () => api<ProductWithRelease[]>('products');
export const listReleases = (productId: string) => api<ReleaseList>(base(productId));
export const registerRelease = (productId: string, body: RegisterReleaseBody) =>
  api<ReleaseChange>(base(productId), { body });
export const makeCurrent = (productId: string, version: string) =>
  api<ReleaseChange>(`${base(productId)}/${enc(version)}/make-current`, { method: 'POST', body: {} });
export const withdrawRelease = (productId: string, version: string, reason: string) =>
  api<ReleaseChange>(`${base(productId)}/${enc(version)}/withdraw`, { body: { reason } });
export const resyncReleases = (productId: string) =>
  api<ReleaseChange>(`${base(productId)}/resync`, { method: 'POST', body: {} });

/** Makes a picked file name acceptable to the API (^[A-Za-z0-9][A-Za-z0-9._ -]{0,98}\.sppkg$). */
export function safeSppkgName(name: string): string | undefined {
  const stem = name.replace(/\.sppkg$/i, '').replace(/[^A-Za-z0-9._ -]+/g, '-').replace(/^[^A-Za-z0-9]+/, '').slice(0, 90);
  return stem ? `${stem}.sppkg` : undefined;
}

/** Uploads the package to /check with progress (fetch cannot report upload progress, so XHR). */
export async function uploadPackageForCheck(
  productId: string,
  file: File,
  onProgress: (fraction: number, uploadDone: boolean) => void,
): Promise<PackageCheck> {
  const token = await engineerToken();
  const safe = safeSppkgName(file.name);
  const url = `${apiBaseUrl}/${base(productId)}/check${safe ? `?fileName=${enc(safe)}` : ''}`;
  return new Promise<PackageCheck>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total, false); };
    xhr.upload.onload = () => onProgress(1, true);
    xhr.onerror = () => reject(new ApiCallError(0, 'NETWORK', 'The Deployment Manager API could not be reached.'));
    xhr.onload = () => {
      let json: { success?: boolean; data?: PackageCheck; error?: { code: string; message: string; detail?: unknown } } | null = null;
      try { json = JSON.parse(xhr.responseText); } catch { json = null; }
      if (xhr.status >= 200 && xhr.status < 300 && json?.success && json.data) { resolve(json.data); return; }
      reject(new ApiCallError(xhr.status, json?.error?.code ?? 'HTTP', json?.error?.message ?? `The API returned HTTP ${xhr.status}.`, json?.error?.detail));
    };
    xhr.send(file);
  });
}

/** Downloads a release package with the engineer's token and hands it to the browser. */
export async function downloadRelease(productId: string, release: Release): Promise<void> {
  const token = await engineerToken();
  const res = await fetch(`${apiBaseUrl}/${base(productId)}/${enc(release.version)}/package`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const json = await res.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new ApiCallError(res.status, json?.error?.code ?? 'HTTP', json?.error?.message ?? `The download failed (HTTP ${res.status}).`);
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = release.fileName || `${productId}-${release.version}.sppkg`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export { compareVersions, readInstallVersions } from '../releases/installCounts';
export type { InstallVersionInfo } from '../releases/installCounts';
